'use client'

import { cn } from '@/lib/tailwind'
import {
  CircleNotchIcon,
  XIcon,
  CheckIcon,
  ArrowClockwiseIcon,
} from '@phosphor-icons/react'
import type { MediaKind } from '@/entries'
import { ENTRY_PRESENTATION } from '@/entries'
import { Skeleton, SkeletonText } from '@/ui/skeleton'

interface LoadingStateProps {
  type: MediaKind
  className?: string
}

/**
 * Loading state while enriching media - skeleton version
 */
export function LoadingState({ type, className }: LoadingStateProps) {
  const colors = ENTRY_PRESENTATION[type]

  return (
    <div
      className={cn(
        'space-y-3 rounded-lg border p-4',
        colors.combined,
        className,
      )}
      role="status"
      aria-live="polite"
    >
      {/* Poster/cover image skeleton */}
      <Skeleton className="h-32 w-full" />
      {/* Title skeleton */}
      <Skeleton className="h-5 w-3/4" />
      {/* Metadata skeletons */}
      <SkeletonText lines={2} className="text-sm" />
      {/* Action buttons placeholder */}
      <div className="flex gap-2 pt-2">
        <Skeleton className="h-8 w-20" />
        <Skeleton className="h-8 w-20" />
      </div>
    </div>
  )
}

interface EmptyStateProps {
  type: MediaKind
  name: string
  error?: string | null
  onRetry?: () => void
  onDismiss: () => void
  onConfirm: () => void
  isSubmitting?: boolean
  disabled?: boolean
  className?: string
}

/**
 * Empty state when no enriched data is available
 */
export function EmptyState({
  type,
  name,
  error,
  onRetry,
  onDismiss,
  onConfirm,
  isSubmitting = false,
  disabled = false,
  className,
}: EmptyStateProps) {
  // `type` is a `MediaKind`, a subset of `EntryType`, so the record is total over it and
  // the old `|| …movie` fallbacks were unreachable (#390).
  const colors = ENTRY_PRESENTATION[type]
  const Icon = colors.icon

  // Truncate long names for display
  const truncatedName = name.length > 40 ? `${name.slice(0, 40)}...` : name

  return (
    <div
      className={cn(
        'flex flex-col gap-2 rounded-lg border p-3',
        colors.combined,
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Icon className="h-5 w-5 shrink-0" weight="fill" />
          <div className="min-w-0">
            <span className="block truncate text-sm font-medium" title={name}>
              Add {colors.label}: {truncatedName}
            </span>
            <p className="text-xs opacity-70">
              {error ? 'Lookup failed.' : 'No additional info found.'} You can
              still save it.
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              disabled={disabled}
              className="rounded p-1.5 transition-colors hover:bg-black/10 disabled:opacity-50"
              title="Retry lookup"
              aria-label="Retry lookup"
            >
              <ArrowClockwiseIcon className="h-4 w-4" />
            </button>
          )}
          <button
            type="button"
            onClick={onDismiss}
            disabled={disabled}
            className="rounded p-1.5 transition-colors hover:bg-black/10 disabled:opacity-50"
            title="Dismiss"
            aria-label="Dismiss"
          >
            <XIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={disabled}
            className="rounded p-1.5 transition-colors hover:bg-black/10 disabled:opacity-50"
            title="Confirm and save"
            aria-label="Confirm and save"
          >
            {isSubmitting ? (
              <CircleNotchIcon className="h-4 w-4 animate-spin" />
            ) : (
              <CheckIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>
      {error && (
        <p className="text-xs opacity-70" role="alert">
          Error: {error}
        </p>
      )}
    </div>
  )
}
